import Fuse from 'fuse.js';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { UserProfile } from '@gitbook/app-database';
import type { HiveClient } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { getSuspenseSafe, mapSuspenseArray } from '@gitbook/util-suspense-cache';
import { assertNever } from '@gitbook/util-ts';

import { apiResource, type InAppSearchResult, readAPICache, searchCache } from '../../state';
import {
    flattenSpacesInOrgAllContent,
    getOrganizationSpacesWithSites,
    getPrimarySiteForSpace,
    getSpaceFromOrgAllContent,
} from '../Organization';

export type ContentSearchResult =
    | ContentSearchResultSpace
    | ContentSearchResultPage
    | ContentSearchResultSection
    | ContentSearchResultLink;

export interface ContentSearchResultSpace {
    type: 'space';
    key: string;
    title: string;
    space: api.Space;
    site: ContentSearchResultSite | undefined;
    groups: ContentSearchGroup[];
}

export interface ContentSearchResultPage {
    type: 'page';
    key: string;
    title: string;
    path: string;
    space: api.Space;
    site: ContentSearchResultSite | undefined;
    revision: hiveCore.Revision;
    page: hiveCore.RevisionPage;
    groups: ContentSearchGroup[];
}

export interface ContentSearchResultSection {
    type: 'section';
    key: string;
    title: string;
    body: string;
    space: api.Space;
    site: ContentSearchResultSite | undefined;
    revision: hiveCore.Revision;
    page: hiveCore.RevisionPage;
    path: string;
    anchor: string | undefined;
    groups: ContentSearchGroup[];
}

/** A link page pointing outside GitBook. */
export interface ContentSearchResultLink {
    type: 'link';
    key: string;
    title: string;
    url: string;
    space: api.Space;
    page: hiveCore.RevisionPage;
    groups: ContentSearchGroup[];
}

/**
 * Site context used to open search results through the site-centric route.
 */
export interface ContentSearchResultSite {
    site: api.Site;
    siteSpace: api.SiteSpace;
}

export type ContentSearchGroup =
    | ContentSearchGroupOwner
    | ContentSearchGroupSpace
    | ContentSearchGroupPage;

export type ContentSearchGroupOwner = ContentSearchGroupUser | ContentSearchGroupOrganization;

interface ContentSearchGroupUser {
    type: 'user';
    user: UserProfile;
}

interface ContentSearchGroupOrganization {
    type: 'organization';
    organization: api.Organization;
}

export interface ContentSearchGroupSpace {
    type: 'space';
    space: api.Space;
}

export interface ContentSearchGroupPage {
    type: 'page';
    space: api.Space;
    page: hiveCore.RevisionPage;
}

type SearchResultsContext = {
    organization: string | null;
    primarySitesBySpace: Map<string, ContentSearchResultSite>;
    hiveClient: HiveClient;
};

type InAppSearchEntryObject =
    | InAppSearchResult
    | {
          kind: 'space';
          space: api.Space;
      };

/**
 * Search spaces from organization content, filtering by title and prioritizing exact matches.
 */
export function searchSpaces(
    query: string,
    orgAllContent: api.OrganizationAllContent
): {
    kind: 'space';
    space: api.Space;
}[] {
    if (!query) {
        return orgAllContent.spaces
            .map((item) => ('space' in item ? item.space : null))
            .filter((space): space is api.Space => Boolean(space))
            .map((space) => ({ kind: 'space', space }));
    }

    return getOrganizationSpacesFuse(orgAllContent)
        .search(query)
        .map(({ item }) => ({ kind: 'space', space: item }));
}

const organizationSpacesFuseCache = new WeakMap<api.OrganizationAllContent, Fuse<api.Space>>();
function getOrganizationSpacesFuse(orgAllContent: api.OrganizationAllContent) {
    const cachedOrganizationSpacesFuse = organizationSpacesFuseCache.get(orgAllContent);
    if (cachedOrganizationSpacesFuse) {
        return cachedOrganizationSpacesFuse;
    }

    const allSpaces = flattenSpacesInOrgAllContent(orgAllContent);
    const fuse = new Fuse(allSpaces, {
        // Reduce default 0.6 threshold for better matching
        threshold: 0.35,
        keys: ['title'],
    });

    organizationSpacesFuseCache.set(orgAllContent, fuse);
    return fuse;
}

/**
 * Fetch and compute a list of content search results (pages, spaces, sections).
 * Results are grouped smartly by space and page; and provided as a flat list.
 *
 * Note: Results are filtered to remove "dummy" entries caused by backend limitations.
 * See `filterDummyResults` for details.
 */
export function useContentSearchResults({
    organizationId,
    query,
    searchQuery = query,
}: {
    organizationId: string | null;
    query: string;
    searchQuery?: string;
}): ContentSearchResult[] {
    const results =
        organizationId && searchQuery
            ? searchCache.read({ organizationId, query: searchQuery })
            : [];
    const hiveClient = useHiveClient();
    const organizationContent = organizationId
        ? readAPICache(apiResource('getOrganizationAllContent', { organizationId }), {
              staleWhileRevalidate: true,
          })
        : null;

    const spaceSearchResults = React.useMemo(
        () => (organizationContent ? searchSpaces(query, organizationContent) : []),
        [query, organizationContent]
    );

    const primarySitesBySpace = React.useMemo(() => {
        if (!organizationContent) {
            return new Map<string, ContentSearchResultSite>();
        }

        return new Map(
            getOrganizationSpacesWithSites(organizationContent).flatMap(({ space, sites }) => {
                const primarySite = getPrimarySiteForSpace(sites);
                return primarySite ? [[space.id, primarySite]] : [];
            })
        );
    }, [organizationContent]);

    return React.useMemo(() => {
        const mappedResults = mapSearchResults([...results, ...spaceSearchResults], {
            organization: organizationId,
            primarySitesBySpace,
            hiveClient,
        });
        return filterDummyResults(mappedResults, query);
    }, [results, spaceSearchResults, organizationId, primarySitesBySpace, hiveClient, query]);
}

/**
 * Build a tree of groups of results.
 */
function mapSearchResults(searchResults: InAppSearchEntryObject[], context: SearchResultsContext) {
    const items = mapSuspenseArray(searchResults, (entry: InAppSearchEntryObject) =>
        mapSearchEntryObject(entry, context)
    );
    const results: ContentSearchResult[] = [];

    const keys = new Set<string>();
    const groupIndexes = new Map<string, number>();

    const locateLastFromGroup = (groups: ContentSearchGroup[]): number | null => {
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup) {
            return null;
        }

        const index = groupIndexes.get(getGroupId(lastGroup));
        if (index === undefined) {
            return locateLastFromGroup(groups.slice(0, -1));
        }

        return index;
    };

    /** Insert an item after the last from a common group or replace it if needed */
    const insertItem = (item: ContentSearchResult) => {
        if (keys.has(item.key)) {
            // Replace the item if it already exists
            const index = results.findIndex((result) => result.key === item.key);
            results.splice(index, 1, item);
            return;
        }

        keys.add(item.key);

        const insertAt = locateLastFromGroup(item.groups) || results.length;
        results.splice(insertAt, 0, item);

        // Keep track of group indexes.
        const existingGroups = item.groups.map((group) => {
            const groupId = getGroupId(group);
            groupIndexes.set(groupId, insertAt + 1);
            return groupId;
        });

        // Re-index the _other_ group indexes after the insertion
        groupIndexes.forEach((index, groupId) => {
            if (index > insertAt && !existingGroups.includes(groupId)) {
                groupIndexes.set(groupId, index + 1);
            }
        });
    };

    items.forEach((item) => {
        if (!item) {
            return;
        }

        switch (item.type) {
            case 'page':
            case 'space': {
                insertItem(item);
                break;
            }
            case 'section':
            case 'link': {
                insertItem(item);
                break;
            }
            default:
                assertNever(item);
        }
    });

    return results;
}

function mapSearchEntryObject(
    entry: InAppSearchEntryObject,
    context: SearchResultsContext
): ContentSearchResult | null {
    if ('kind' in entry) {
        return {
            type: 'space',
            key: `space:${entry.space.id}`,
            title: entry.space.title,
            space: entry.space,
            site: context.primarySitesBySpace.get(entry.space.id),
            groups: [],
        };
    }

    switch (entry.index) {
        case 'pages': {
            const resolved = resolvePage(
                {
                    spaceId: entry.object.space,
                    pageId: entry.object.page,
                    organizationId: entry.object.organization || null,
                },
                context
            );
            if (!resolved) {
                return null;
            }
            if (resolved.page.type === hiveCore.RevisionPageType.Link) {
                return mapLinkPage({ ...resolved, page: resolved.page }, context);
            }

            return {
                type: 'page',
                key: `page:${resolved.space.id}:${resolved.page.id}`,
                title: entry.object.title,
                path: hiveCore.getAbsolutePagePathInRevision(resolved.revision, resolved.page.id),
                space: resolved.space,
                site: context.primarySitesBySpace.get(resolved.space.id),
                revision: resolved.revision,
                page: resolved.page,
                groups: getGroupsForPage(resolved.space, resolved.revision, resolved.page),
            };
        }
        case 'sections': {
            const resolved = resolvePage(
                {
                    spaceId: entry.object.space,
                    pageId: entry.object.page,
                    organizationId: entry.object.organization || null,
                },
                context
            );
            if (!resolved) {
                return null;
            }
            if (resolved.page.type === hiveCore.RevisionPageType.Link) {
                return mapLinkPage({ ...resolved, page: resolved.page }, context);
            }

            return {
                type: 'section',
                key: `section:${entry.object.objectID}`,
                title: entry.object.title,
                body: entry.object.body,
                space: resolved.space,
                site: context.primarySitesBySpace.get(resolved.space.id),
                revision: resolved.revision,
                page: resolved.page,
                path: hiveCore.getAbsolutePagePathInRevision(resolved.revision, resolved.page.id),
                anchor: entry.object.anchor,
                groups: [
                    ...getGroupsForPage(resolved.space, resolved.revision, resolved.page),
                    {
                        type: 'page',
                        space: resolved.space,
                        page: resolved.page,
                    },
                ],
            };
        }
        default:
            assertNever(entry);
    }
}

function resolvePage(
    entry: { spaceId: string; pageId: string; organizationId: string | null },
    { organization, hiveClient }: SearchResultsContext
): { space: api.Space; revision: hiveCore.Revision; page: hiveCore.RevisionPage } | null {
    const { spaceId, pageId, organizationId = organization } = entry;
    if (!organizationId) {
        return null;
    }

    const organizationAllContent = readAPICache(
        apiResource('getOrganizationAllContent', { organizationId }),
        { staleWhileRevalidate: true }
    );

    const spaceItem = getSpaceFromOrgAllContent(organizationAllContent, spaceId);
    if (!spaceItem) {
        return null;
    }

    const { space } = spaceItem.item;
    if (!space) {
        return null;
    }

    const sourceRevision = getSuspenseSafe(() =>
        hiveClient.store.getRevision(spaceId, space.revision, { metadata: false })
    );
    if (!sourceRevision) {
        return null;
    }

    const computedRevision = getSuspenseSafe(() =>
        hiveClient.getContentComputer(sourceRevision.contentId).getCompleteRevision(sourceRevision)
    );

    const revision = computedRevision?.revision;
    const page = revision?.pages[pageId];
    if (!revision || !page) {
        return null;
    }

    return { space, revision, page };
}

/**
 * Map a link page to what it points at: an external URL, or the document it links to in the same
 * space. Other targets are not indexed, so they have no result.
 *
 * @internal Exported for testing only
 */
export function mapLinkPage(
    resolved: { space: api.Space; revision: hiveCore.Revision; page: hiveCore.RevisionPageLink },
    context: Pick<SearchResultsContext, 'primarySitesBySpace'>
): ContentSearchResultLink | ContentSearchResultPage | null {
    const { space, revision, page } = resolved;
    const { target } = page;

    if (target.kind === 'url') {
        return {
            type: 'link',
            key: `link:${space.id}:${page.id}`,
            title: page.title,
            url: target.url,
            space,
            page,
            groups: getGroupsForPage(space, revision, page),
        };
    }

    if (target.kind === 'page' && (!target.space || target.space === space.id)) {
        const targetPage = revision.pages[target.page];
        if (targetPage?.type !== hiveCore.RevisionPageType.Document) {
            return null;
        }

        // Keyed by the target so it merges with the target page's own result.
        return {
            type: 'page',
            key: `page:${space.id}:${targetPage.id}`,
            title: page.title,
            path: hiveCore.getAbsolutePagePathInRevision(revision, targetPage.id),
            space,
            site: context.primarySitesBySpace.get(space.id),
            revision,
            page: targetPage,
            groups: getGroupsForPage(space, revision, targetPage),
        };
    }

    return null;
}

function getGroupsForPage(
    space: api.Space,
    revision: hiveCore.Revision,
    page: hiveCore.RevisionPage
): ContentSearchGroup[] {
    const ancestors = hiveCore.getPageAncestorsInRevision(revision, page.id);
    const ancestorGroup =
        ancestors[0]?.type === hiveCore.RevisionPageType.Group ? ancestors[0] : null;

    return [
        {
            type: 'space',
            space,
        },
        ...(ancestorGroup
            ? ([
                  {
                      type: 'page',
                      space,
                      page: ancestorGroup,
                  },
              ] as ContentSearchGroupPage[])
            : []),
    ];
}

function getGroupId(group: ContentSearchGroup): string {
    switch (group.type) {
        case 'user':
            return `user:${group.user.uid}`;
        case 'organization':
            return `organization:${group.organization.id}`;
        case 'space':
            return `space:${group.space.id}`;
        case 'page':
            return `page:${group.space.id}:${group.page.id}`;
    }
}

/**
 * The search API only stores _sections_ in Turbopuffer, not pages. Pages are just
 * a way of organizing section results. This leads to three scenarios:
 *
 * 1. Query matches both page title/path AND section content -> show both
 * 2. Query matches content in a section but NOT the page -> we want to show section only (so we filter out dummy page)
 * 3. Query matches page but NOT any sections -> we want to show page only (so we filter out dummy sections)
 *
 * The API doesn't provide a "reason" for why a result was included, so we must
 * post-process results client-side by checking if the query actually appears in
 * the relevant fields.
 *
 * Aligned on below solution with the search-team, the limitation could be overcome by changes
 * in turbopuffer which will be discussed within the team, but for now this is the best solution esp. since search also powers GBO.
 *
 * @internal Exported for testing only
 */
export function filterDummyResults(
    results: ContentSearchResult[],
    query: string
): ContentSearchResult[] {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) {
        return results;
    }

    // Tokenize the query into individual words to match backend behavior.
    // The backend matches tokens independently, so "foo bar" matches text containing
    // both "foo" and "bar" anywhere (not necessarily contiguously).
    const queryTokens = normalizedQuery.split(/\s+/).filter((token) => token.length > 0);

    return results.filter((item) => {
        switch (item.type) {
            case 'space':
                // Spaces are searched client-side with Fuse.js, so they're always real matches
                return true;
            case 'page':
                // Filter out pages that don't actually match the query in title or path.
                // These dummy page results appear when only their sections matched.
                return queryMatchesText(queryTokens, item.title, item.path);
            case 'section':
                // Filter out sections that don't actually match the query in title or body.
                // These dummy section results appear when only the parent page title matched
                // (the API arbitrarily picks sections to return since it has no "page results").
                return queryMatchesText(queryTokens, item.title, item.body);
            default:
                return true;
        }
    });
}

/**
 * Check if all query tokens appear in any of the provided text fields.
 * Each token must appear in at least one of the fields.
 *
 * @internal Exported for testing only
 */
export function queryMatchesText(queryTokens: string[], ...texts: string[]): boolean {
    const combinedText = texts.join(' ').toLowerCase();
    return queryTokens.every((token) => combinedText.includes(token));
}
